'use client';

import { BoxCTA } from '@lichtblick/photon/atoms/box-cta/box-cta';
import { HeatingS } from '@lichtblick/photon/icons/energy/heating-s';
import { useTracker } from '@lichtblick/tracker';
import { type FormEvent, useState } from 'react';
import { buildUrl } from '../../helpers/links';
import type { HeatingCalculatorType } from '../../types/storyblok';
import { CityInput } from './CityInput';
import { CalculatorLayout } from './calculator-layout';
import styles from './calculator-layout.module.scss';
import type { BaseCalculatorProps } from './OAllInOneCalculator';
import { CalculatorTranslations } from './translations';
import { useVpCodes } from './useVpCodes';

type FormValues = {
  city?: string;
  postcode?: string;
  sourceOfHeat?: string;
};

type HeatingCalculatorProps = BaseCalculatorProps & HeatingCalculatorType;

export const HeatingCalculator: React.FC<HeatingCalculatorProps> = ({
  _uid,
  checkoutUrl,
  chip,
  directSalesPartnerCode,
  headline,
  isActionTriggerDisabled,
  powerCloudCampaignId,
  sourceOfHeat,
  useDirectSalesPartnerCodeSitewide,
}) => {
  const { trackCustomEvent } = useTracker();

  const { computeVpCodes } = useVpCodes();
  const [values, setValues] = useState<FormValues>({
    sourceOfHeat: sourceOfHeat,
  });

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    trackCustomEvent({
      eventName: 'tariff_calculator_show_tariffs',
      customProps: {
        product: 'WärmeStrom',
        product_variant: values.sourceOfHeat === 'heat_pump' ? 'Wärmepumpe' : 'Nachtspeicherheizung',
        zip_code: values.postcode,
      },
    });

    const vpCodes = computeVpCodes(directSalesPartnerCode, useDirectSalesPartnerCodeSitewide);

    const params = new URLSearchParams({
      ...(values?.city && { ort: values.city }),
      ...(values?.postcode && { plz: values.postcode }),
      ...(values?.sourceOfHeat && { heizquelle: values.sourceOfHeat }),
      ...(powerCloudCampaignId && { cam: powerCloudCampaignId }),
      ...(vpCodes?.vp && { vp: vpCodes.vp }),
      ...(vpCodes?.uvp && { uvp: vpCodes.uvp }),
    });

    location.assign(`${buildUrl(checkoutUrl) ?? '/waermestrom-checkout/'}?${params.toString()}`);
  };

  return (
    <CalculatorLayout
      chip={chip}
      data-testid={`calculator-heat`}
      headerColor="LightBerry"
      headline={headline}
      icon={<HeatingS />}
      id={_uid}
      isActionTriggerDisabled={isActionTriggerDisabled}
    >
      <form className={styles['calculator-content']} method="get" onSubmit={handleSubmit}>
        <CityInput onChange={(city, postcode) => setValues((prev) => ({ ...prev, city: city, postcode: postcode }))} />
        <BoxCTA
          as="button"
          data-testid="calc-submit"
          type="submit"
          wide
          data-dd-action-name="Calculator submit: WärmeStrom"
        >
          {CalculatorTranslations.heatSubmitLabel}
        </BoxCTA>
      </form>
    </CalculatorLayout>
  );
};
